import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useScrollToHash } from "@/hooks/useScrollToHash";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy load product pages to reduce initial bundle size
const ThankYou = lazy(() => import("./pages/ThankYou"));
const RomanShades = lazy(() => import("./pages/RomanShades"));
const PlantationWoodShutters = lazy(() => import("./pages/PlantationWoodShutters"));
const CellularShades = lazy(() => import("./pages/CellularShades"));
const RollerShades = lazy(() => import("./pages/RollerShades"));
const SunScreens = lazy(() => import("./pages/SunScreens"));
const MotorizedShades = lazy(() => import("./pages/MotorizedShades"));
const SmartDrapes = lazy(() => import("./pages/SmartDrapes"));
const ZebraShades = lazy(() => import("./pages/ZebraShades"));
const FauxWoodBlinds = lazy(() => import("./pages/FauxWoodBlinds"));
const WoodBlinds = lazy(() => import("./pages/WoodBlinds"));
const PatioShades = lazy(() => import("./pages/PatioShades"));

const queryClient = new QueryClient();

function ScrollToHashHandler() {
  useScrollToHash();
  return null;
}

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToHashHandler />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/roman-shades" element={<RomanShades />} />
            <Route path="/plantation-wood-shutters" element={<PlantationWoodShutters />} />
            <Route path="/cellular-shades" element={<CellularShades />} />
            <Route path="/roller-shades" element={<RollerShades />} />
            <Route path="/sun-screens" element={<SunScreens />} />
            <Route path="/motorized-shades" element={<MotorizedShades />} />
            <Route path="/smart-drapes" element={<SmartDrapes />} />
            <Route path="/zebra-shades" element={<ZebraShades />} />
            <Route path="/faux-wood-blinds" element={<FauxWoodBlinds />} />
            <Route path="/wood-blinds" element={<WoodBlinds />} />
            <Route path="/patio-shades" element={<PatioShades />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
